import { computed, ref, set, unref } from "@vue/composition-api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import logger from "@/modules/logger";
import { EVENTS } from "@/const";
import {
  FullCreatorVideoPostController,
  FullCreatorVideoPostRetryState,
} from "@/views/PageBecomeCreator/components/FullCreatorForm/hooks/useFullCreatorVideoPostController";

export enum FullCreatorApplicationUploadStatus {
  IDLE = "IDLE",
  UPLOADING = "UPLOADING",
  PUBLISHING = "PUBLISHING",
  WAITING_FOR_APPLICATION = "WAITING_FOR_APPLICATION",
  SUBMITTING = "SUBMITTING",
  RETRYING = "RETRYING",
  ERROR = "ERROR",
  APPLICATION_ERROR = "APPLICATION_ERROR",
  SUCCESS = "SUCCESS",
}

export enum FullCreatorPostJobStatus {
  UPLOADING = "UPLOADING",
  UPLOAD_FAILED = "UPLOAD_FAILED",
  READY = "READY",
  PUBLISHING = "PUBLISHING",
  PUBLISHED = "PUBLISHED",
  PUBLISH_FAILED = "PUBLISH_FAILED",
}

export interface FullCreatorPostJob {
  postId: string;
  status: FullCreatorPostJobStatus;
  error: unknown | null;
}

export interface FullCreatorPostUploadProgress {
  totalFiles: number;
  uploadedFileIds: string[];
}

type SubmitApplication = () => Promise<void>;
type AfterApplicationSubmitted = () => Promise<void>;
type AfterPublishingFailed = () => Promise<void> | void;

let videoControllers: Record<string, FullCreatorVideoPostController> = {};
const videoControllerRetryStates = ref<Record<string, FullCreatorVideoPostRetryState>>({});
const videoControllerGeneration = ref(0);
const postJobs = ref<Record<string, FullCreatorPostJob>>({});
const postUploadProgress = ref<Record<string, FullCreatorPostUploadProgress>>({});
const activePostIds = ref<string[]>([]);
const status = ref<FullCreatorApplicationUploadStatus>(FullCreatorApplicationUploadStatus.IDLE);
const publishingPromise = ref<Promise<void> | null>(null);
const submissionPromise = ref<Promise<void> | null>(null);
const submissionRequested = ref(false);
const applicationSubmitted = ref(false);

let processingRunId = 0;
let sessionGeneration = 0;
let applicationSubmitter: SubmitApplication | null = null;
let afterApplicationSubmitted: AfterApplicationSubmitted | null = null;
let submissionInFlight: Promise<void> | null = null;
let resolveSubmission: (() => void) | null = null;

const activeControllers = computed(() => activePostIds.value.map(postId => videoControllers[postId]).filter(Boolean));

const totalFiles = computed(() =>
  activePostIds.value.reduce((total, postId) => total + (postUploadProgress.value[postId]?.totalFiles ?? 0), 0)
);

const uploadedFilesNumber = computed(() =>
  activePostIds.value.reduce(
    (total, postId) => total + (postUploadProgress.value[postId]?.uploadedFileIds.length ?? 0),
    0
  )
);

const errorUploadingFile = computed(() =>
  activeControllers.value.some(controller => unref(controller.errorUploadingFile))
);
const isUploadingFiles = computed(() => activeControllers.value.some(controller => unref(controller.isUploadingFiles)));
const isActive = computed(() =>
  [
    FullCreatorApplicationUploadStatus.UPLOADING,
    FullCreatorApplicationUploadStatus.PUBLISHING,
    FullCreatorApplicationUploadStatus.SUBMITTING,
  ].includes(status.value)
);

const setJobState = (
  job: { status: FullCreatorPostJobStatus; error: unknown | null },
  jobStatus: FullCreatorPostJobStatus,
  error: unknown = null
) => {
  set(job, "status", jobStatus);
  set(job, "error", error);
};

const ensureSubmissionPromise = () => {
  if (!submissionPromise.value) {
    submissionPromise.value = new Promise<void>(resolve => {
      resolveSubmission = resolve;
    });
  }
  return submissionPromise.value;
};

const canSubmitApplication = () => {
  if (!activePostIds.value.length) {
    return (
      publishingPromise.value === null || status.value === FullCreatorApplicationUploadStatus.WAITING_FOR_APPLICATION
    );
  }
  return activePostIds.value.every(postId => postJobs.value[postId]?.status === FullCreatorPostJobStatus.PUBLISHED);
};

const completeApplicationSubmission = async () => {
  if (
    !submissionRequested.value ||
    applicationSubmitted.value ||
    submissionInFlight ||
    !applicationSubmitter ||
    !canSubmitApplication()
  ) {
    return submissionPromise.value;
  }

  status.value = FullCreatorApplicationUploadStatus.SUBMITTING;
  const currentSessionGeneration = sessionGeneration;
  const submit = applicationSubmitter;
  const finalize = afterApplicationSubmitted;
  submissionInFlight = (async () => {
    try {
      await submit();
      if (currentSessionGeneration !== sessionGeneration) return;
      applicationSubmitted.value = true;
    } catch (error) {
      if (currentSessionGeneration !== sessionGeneration) return;
      status.value = FullCreatorApplicationUploadStatus.APPLICATION_ERROR;
      return;
    }

    if (finalize) {
      try {
        await finalize();
        if (currentSessionGeneration !== sessionGeneration) return;
      } catch (error) {
        if (currentSessionGeneration !== sessionGeneration) return;
        // The application is already accepted. A refresh failure must never submit it again.
        logger.debug("Full Creator application post-submit refresh failed", { error });
      }
    }

    if (currentSessionGeneration !== sessionGeneration) return;
    status.value = FullCreatorApplicationUploadStatus.SUCCESS;
    emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("Application sent"), timeout: 2000 });
    resolveSubmission?.();
    resolveSubmission = null;
  })().finally(() => {
    submissionInFlight = null;
  });

  return submissionInFlight;
};

export default function useFullCreatorApplicationUpload() {
  const markFileUploaded = (postId: string, fileId: string) => {
    const progress = postUploadProgress.value[postId];
    if (!progress || progress.uploadedFileIds.includes(fileId)) return;
    set(progress, "uploadedFileIds", [...progress.uploadedFileIds, fileId]);
  };

  const registerVideoController = (postId: string, controller: FullCreatorVideoPostController) => {
    videoControllers[postId] = controller;
  };

  const unregisterVideoController = (postId: string) => {
    if (activePostIds.value.includes(postId)) return;
    const { [postId]: _, ...rest } = videoControllers;
    videoControllers = rest;
  };

  const getVideoController = (postId: string) => videoControllers[postId];

  const getVideoControllerRetryState = (postId: string) => videoControllerRetryStates.value[postId];

  const isPostPublishing = (postId: string) => activePostIds.value.includes(postId);

  const startVideoPublishing = (postIds: string[], afterPublishingFailed?: AfterPublishingFailed) => {
    const missingControllerPostIds = postIds.filter(postId => !videoControllers[postId]);
    if (missingControllerPostIds.length) {
      status.value = FullCreatorApplicationUploadStatus.PUBLISHING;
      const promise = (async () => {
        try {
          await afterPublishingFailed?.();
        } catch (error) {
          logger.debug("Full Creator failed to save upload-error resume step", { error });
        }
        status.value = FullCreatorApplicationUploadStatus.ERROR;
        throw new Error(`Full Creator application video controllers missing: ${missingControllerPostIds.join(", ")}`);
      })();
      promise.catch(() => undefined);
      publishingPromise.value = promise;
      return promise;
    }

    activePostIds.value = [...postIds];
    postUploadProgress.value = postIds.reduce<Record<string, FullCreatorPostUploadProgress>>((result, postId) => {
      const files = unref(videoControllers[postId].postFileDataList);
      result[postId] = {
        totalFiles: files.length,
        uploadedFileIds: files.filter(file => !file.uploading).map(file => file.id),
      };
      return result;
    }, {});
    videoControllerRetryStates.value = {};
    const runId = ++processingRunId;
    for (const postId of postIds) {
      const existingJob = postJobs.value[postId];
      if (existingJob) {
        setJobState(existingJob, FullCreatorPostJobStatus.UPLOADING);
      } else {
        set(postJobs.value, postId, {
          postId,
          status: FullCreatorPostJobStatus.UPLOADING,
          error: null,
        });
      }
    }
    status.value = isUploadingFiles.value
      ? FullCreatorApplicationUploadStatus.UPLOADING
      : FullCreatorApplicationUploadStatus.PUBLISHING;

    const promise = (async () => {
      const jobs = postIds.map(postId => postJobs.value[postId]);
      const uploadResults = await Promise.allSettled(
        jobs.map(job => videoControllers[job.postId].waitForUploads?.() ?? Promise.resolve())
      );
      if (runId !== processingRunId) return;

      uploadResults.forEach((result, index) => {
        if (result.status === "rejected") {
          videoControllers[jobs[index].postId].cleanupFailedUploads?.();
        }
        setJobState(
          jobs[index],
          result.status === "fulfilled" ? FullCreatorPostJobStatus.READY : FullCreatorPostJobStatus.UPLOAD_FAILED,
          result.status === "rejected" ? result.reason : null
        );
      });

      const readyJobs = jobs.filter(job => job.status === FullCreatorPostJobStatus.READY);
      status.value = FullCreatorApplicationUploadStatus.PUBLISHING;
      readyJobs.forEach(job => setJobState(job, FullCreatorPostJobStatus.PUBLISHING));
      const publishResults = await Promise.allSettled(readyJobs.map(job => videoControllers[job.postId].publish()));
      if (runId !== processingRunId) return;

      publishResults.forEach((result, index) => {
        setJobState(
          readyJobs[index],
          result.status === "fulfilled" ? FullCreatorPostJobStatus.PUBLISHED : FullCreatorPostJobStatus.PUBLISH_FAILED,
          result.status === "rejected" ? result.reason : null
        );
      });

      const failed = jobs.some(job =>
        [FullCreatorPostJobStatus.UPLOAD_FAILED, FullCreatorPostJobStatus.PUBLISH_FAILED].includes(job.status)
      );
      if (failed) {
        try {
          await afterPublishingFailed?.();
        } catch (error) {
          logger.debug("Full Creator failed to save upload-error resume step", { error });
        }
        if (runId !== processingRunId) return;
        status.value = FullCreatorApplicationUploadStatus.ERROR;
        throw new Error("Full Creator application videos publishing failed");
      }

      status.value = FullCreatorApplicationUploadStatus.WAITING_FOR_APPLICATION;
      void completeApplicationSubmission();
    })();
    promise.catch(() => undefined);
    publishingPromise.value = promise;

    return promise;
  };

  const submitApplicationAfterPublishing = (submit: SubmitApplication, finalize?: AfterApplicationSubmitted) => {
    submissionRequested.value = true;
    applicationSubmitter = submit;
    afterApplicationSubmitted = finalize ?? null;
    const promise = ensureSubmissionPromise();
    void completeApplicationSubmission();
    return promise;
  };

  const retryApplicationSubmission = () => {
    if (status.value !== FullCreatorApplicationUploadStatus.APPLICATION_ERROR) return submissionPromise.value;
    status.value = FullCreatorApplicationUploadStatus.WAITING_FOR_APPLICATION;
    void completeApplicationSubmission();
    return submissionPromise.value;
  };

  const startRetryEditing = () => {
    if (status.value === FullCreatorApplicationUploadStatus.RETRYING) return;
    if (status.value !== FullCreatorApplicationUploadStatus.ERROR) return;
    processingRunId++;
    sessionGeneration++;
    resolveSubmission?.();
    resolveSubmission = null;
    publishingPromise.value = null;
    submissionPromise.value = null;
    submissionRequested.value = false;
    applicationSubmitted.value = false;
    applicationSubmitter = null;
    afterApplicationSubmitted = null;
    submissionInFlight = null;
    postUploadProgress.value = {};
    const retryStates: Record<string, FullCreatorVideoPostRetryState> = {};
    activePostIds.value.forEach(postId => {
      const controller = videoControllers[postId];
      const retryState = controller?.getRetryState?.();
      if (retryState) retryStates[postId] = retryState;
    });
    videoControllerRetryStates.value = retryStates;
    // Composition API computeds created by a card are disposed with that card. Reusing
    // the controller would preserve refs but leave derived UI state permanently stale.
    videoControllers = {};
    videoControllerGeneration.value++;
    status.value = FullCreatorApplicationUploadStatus.RETRYING;
  };

  const discardVideoPost = (postId: string) => {
    activePostIds.value = activePostIds.value.filter(id => id !== postId);
    const { [postId]: _, ...remainingControllers } = videoControllers;
    videoControllers = remainingControllers;
    const { [postId]: __, ...remainingJobs } = postJobs.value;
    postJobs.value = remainingJobs;
    const { [postId]: ___, ...remainingProgress } = postUploadProgress.value;
    postUploadProgress.value = remainingProgress;
    const { [postId]: ____, ...remainingRetryStates } = videoControllerRetryStates.value;
    videoControllerRetryStates.value = remainingRetryStates;
  };

  const reset = () => {
    processingRunId++;
    sessionGeneration++;
    resolveSubmission?.();
    resolveSubmission = null;
    activePostIds.value = [];
    videoControllers = {};
    videoControllerRetryStates.value = {};
    videoControllerGeneration.value++;
    postJobs.value = {};
    postUploadProgress.value = {};
    status.value = FullCreatorApplicationUploadStatus.IDLE;
    publishingPromise.value = null;
    submissionPromise.value = null;
    submissionRequested.value = false;
    applicationSubmitted.value = false;
    applicationSubmitter = null;
    afterApplicationSubmitted = null;
    submissionInFlight = null;
  };

  return {
    status,
    postJobs,
    postUploadProgress,
    activePostIds,
    videoControllerGeneration,
    totalFiles,
    uploadedFilesNumber,
    errorUploadingFile,
    isUploadingFiles,
    isActive,
    publishingPromise,
    submissionPromise,
    submissionRequested,
    applicationSubmitted,
    markFileUploaded,
    registerVideoController,
    unregisterVideoController,
    getVideoController,
    getVideoControllerRetryState,
    isPostPublishing,
    startVideoPublishing,
    submitApplicationAfterPublishing,
    retryApplicationSubmission,
    startRetryEditing,
    discardVideoPost,
    reset,
  };
}
